import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import React, { useState, useEffect, useRef } from 'react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { Button, Spinner, SkeletonForm } from '@parsina/framework';
import { ChevronDown } from 'lucide-react';
import { bffMutation, bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import OnboardingShell from './Shell.jsx';
import PrizeStage from './PrizeStage.jsx';
import { useOnboardingStrings } from './onboardingStrings';
import { COUNTRIES, countryForPhone, countryName } from '../../constants/countries';
import { SPECIALTIES_QUERY, OTHER_SPECIALTY, SUBSPECIALTY, groupSpecialties } from './specialtyCatalogue';

// ── GraphQL mutations ─────────────────────────────────────────────────────────

// The doctor's name is no longer asked for, or read from the token.
//
// It used to come from the JWT claims and, when those were empty (a phone +
// SMS-code account carries none), from a field on this form. Both are gone:
// the name on a prescription is the one سازمان نظام پزشکی holds against the
// council number, so the wizard reads it instead of asking anyone.

const LOOKUP_COUNCIL_MEMBER = `
  query LookupCouncilMember($code: String!) {
    lookupCouncilMember(code: $code) {
      status
      member { councilNumber fullName specialty degree memberType province city }
    }
  }
`;

const COMPLETE_STEP1 = `
  mutation CompleteOnboardingStep1($input: OnboardingStep1Input!) {
    completeOnboardingStep1(input: $input) {
      id fullName medicalCode specialty bio
    }
  }
`;

// Which onboarding this account gets, and in which language (PM-0060/0061).
// Both follow from the country of the number the doctor signed in with, and the
// BFF reads that from the platform's own record — this screen only asks.
const ONBOARDING_CONTEXT = `
  query OnboardingContext {
    onboardingContext { foreign phone impliedLanguage }
  }
`;

// The manual form a doctor on a non-Iranian number fills in place of the
// council number. Refused by the BFF for any other account.
const COMPLETE_FOREIGN = `
  mutation CompleteForeignOnboarding($input: ForeignOnboardingInput!) {
    completeForeignOnboarding(input: $input) {
      id fullName medicalCode specialty degree country city selfDeclared bio
    }
  }
`;

// ── Shared UI primitives ──────────────────────────────────────────────────────

// ── The first screen a new doctor sees (finding 12) ─────────────────────────
// It had four defects at once, and they all showed on a phone: raw `<button>`s
// with hand-written gradients, literal px and hex, and a fixed 520px card that
// on a 360px screen is wider than the window. The fix is not a second render
// tree — the three steps are one-field-per-row forms that would be IDENTICAL on
// both surfaces, and a fork of a form is two files to keep in sync for no
// behavioural difference. What was actually wrong is the SHELL (full-bleed and
// docked on a phone, a card on a desktop) and the primitives, so those are what
// changed.

const ErrorBox = ({ msg }) => msg ? (
  <div style={{
    padding: '0.625rem 0.875rem', background: CT.dangerSoft,
    border: `1px solid ${C.redLight}`,
    borderRadius: '0.5rem', fontSize: '0.8125rem', color: CT.dangerInk,
    textAlign: 'start', lineHeight: 1.6,
  }}>{msg}</div>
) : null;

// The kit Button, not a hand-rolled one. `min-h-11` is §6's hit-target floor —
// the old 46px happened to clear it and nothing held it there.
const Btn = ({
  children, onClick, type = 'button', disabled, secondary, fullWidth, danger, testId, ...rest
}) => (
  <Button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    {...rest}
    variant={danger ? 'destructive' : secondary ? 'outline' : 'default'}
    size="lg"
    className={`min-h-11 font-bold whitespace-nowrap${fullWidth ? ' w-full' : ''}${secondary || danger ? '' : ' bg-gradient-brand'}`}
  >{children}</Button>
);

const Field = ({ label, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: C.textSecondary }}>
      {label}{required && <span style={{ color: C.red, marginInlineStart: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const inputBase = {
  height: 40, padding: '0 10px',
  border: `1.5px solid ${C.border}`, borderRadius: 8,
  fontSize: '0.8125rem', fontFamily: 'var(--app-font)',
  color: C.textPrimary, outline: 'none', 
  background: 'white', width: '100%', boxSizing: 'border-box',
};

// The rest of the props reach the `<input>`. Without that, anything a caller
// writes the DOM's way — `dir="ltr"`, `data-testid`, `inputMode`, `autoFocus`
// — is silently swallowed by this wrapper, which is how the council-number
// field ended up reading right-to-left with no test id: both were passed, and
// neither was in the signature. `ltr` stays as the shorthand this file already
// uses; an explicit `dir` wins over it.
const TextInput = ({
  label, value, onChange, onBlur, placeholder, required, type = 'text', ltr, testId, ...rest
}) => (
  <Field label={label} required={required}>
    <input
      type={type}
      data-testid={testId}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      {...rest}
      style={{ ...inputBase, direction: rest.dir || (ltr ? 'ltr' : 'rtl'), ...(rest.style || {}) }}
      onFocus={e => { e.target.style.borderColor = C.navy; }}
      onBlur={e => { e.target.style.borderColor = C.border; onBlur?.(e); }}
    />
  </Field>
);

// Collapsed by default — optional, and editable later in settings. Shared by
// both forms: the council one and the foreign one end the same way.
const BioDisclosure = ({ open, onToggle, value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="onboarding-bio-toggle"
        aria-expanded={open}
        aria-controls="onboarding-bio-panel"
        onClick={onToggle}
        className="min-h-11 w-full justify-between px-2 font-semibold"
        style={{ color: C.textSecondary }}
      >
        <span>{t('auth.onboarding.bioOptional')}</span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </Button>
      {open && (
        <div id="onboarding-bio-panel" style={{ marginTop: 6 }}>
          <textarea
            data-testid="onboarding-bio"
            aria-label={t('auth.onboarding.bioOptional')}
            autoFocus
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={t('settings.profile.bioPlaceholder')}
            rows={3}
            style={{
              border: `1.5px solid ${C.border}`, borderRadius: 8,
              padding: '10px 12px', fontSize: '0.8125rem',
              fontFamily: 'var(--app-font)', color: C.textPrimary,
              outline: 'none', resize: 'vertical',
              width: '100%', boxSizing: 'border-box',
            }}
            onFocus={e => { e.target.style.borderColor = C.navy; }}
            onBlur={e => { e.target.style.borderColor = C.border; }}
          />
        </div>
      )}
    </div>
  );
};

// ── Welcome ───────────────────────────────────────────────────────────────────
//
// The welcome used to be a screen of its own — a card, a one-item checklist and
// a «شروع» button — in front of a form that is itself one field. It is now the
// top of that form: the doctor lands on the council-number field with the
// greeting above it, and there is nothing to press before they can start.
const WelcomeHeader = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="onboarding-welcome" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
        background: `linear-gradient(135deg, ${soft(C.navy, 0.06)}, ${soft(C.navy, 0.16)})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg viewBox="0 0 24 24" fill={C.navyDark} width="24" height="24" aria-hidden="true">
          <path d="M20 6h-2.18c.07-.44.18-.88.18-1.34C18 2.54 15.86.6 13.16.6c-1.6 0-3.05.8-3.93 2.02L12 5.46l2.77-2.84C15.23 2.24 16.15 2 17 2c1.66 0 3 1.34 3 3s-1.34 3-3 3h-3.45L12 9.46l-1.55-1.46H7C5.34 8 4 6.66 4 5s1.34-3 3-3c.85 0 1.77.24 2.23.62L12 5.46 14.84 2.62C13.96 1.4 12.51.6 10.91.6 8.21.6 6 2.54 6 4.66c0 .46.11.9.18 1.34H4C2.9 6 2 6.9 2 8v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 14H4V8h16v12z"/>
        </svg>
      </div>
      <div>
        <div style={{ fontSize: '1.125rem', fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>
          {t('auth.onboarding.welcome')}
        </div>
        <div style={{ fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.8 }}>
          {t('auth.onboarding.welcomeBody')}
        </div>
      </div>
    </div>
  );
};

// ── Stage: Step 1 — the doctor's professional identity ───────────────────────
//
// One field: the کد نظام پزشکی. Everything else on this screen is READ — name,
// degree, specialty, city — from سامانه جستجوی اعضای سازمان نظام پزشکی, which
// is the authority on all of it. The doctor does not type their name and does
// not pick a specialty, and there is no «تأیید / این من نیستم» step: the
// council's record for that number either exists or it does not.
//
// The read-only rendering here is a courtesy, not the rule — the BFF resolves
// the code again and writes the council's values whatever this screen sends
// (see resolvers/onboarding.js). That is what makes "the doctor cannot edit it"
// true rather than merely displayed.

// ── When the استعلام runs ────────────────────────────────────────────────────
//
// There is no «استعلام» button: the lookup starts by itself, off the number of
// DIGITS typed. The council allows ten searches per ten minutes to the whole
// platform — every pod leaves through one address — so a lookup per keystroke
// would spend that allowance on «۱», «۱۲», «۱۲۵» before the first doctor
// finished typing. The thresholds below are what keeps the common case to one
// request:
//
//   • 6+ digits — a current council number is six digits, so the moment it is
//     complete there is nothing to wait for;
//   • 4–5 digits — real, but held by long-registered doctors (`1000`, `25000`
//     and `80000` are members), so those wait for a pause in typing instead. A
//     doctor entering a six-digit number rarely stops for 1.2s mid-number;
//   • fewer — never automatically. Leaving the field still looks up, as before.
const IMMEDIATE_DIGITS = 6;
const AUTO_MIN_DIGITS = 4;
const TYPING_PAUSE_MS = 1200;

/**
 * The digits in what was typed, Persian and Arabic-Indic ones included — a
 * phone keyboard produces «۱۲۵۰۰۰», and that is six digits, not zero. Letters
 * of a profession prefix («د-»، «ع-آ-») are not digits and do not count.
 */
const councilDigits = (value) =>
  String(value ?? '')
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, '');

/** One line of the council's record. Rendered, never edited. */
const RegistryRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: '0.8125rem', lineHeight: 1.9 }}>
    <span style={{ color: C.textSecondary, minWidth: 96, flexShrink: 0 }}>{label}</span>
    <span style={{ color: C.textPrimary, fontWeight: 600 }}>{value}</span>
  </div>
);

const StepDoctorInfo = ({ onDone }) => {
  const { t } = useTranslation();

  const [code, setCode] = useState('');
  const [member, setMember] = useState(null);
  const [looking, setLooking] = useState(false);
  const [bio, setBio] = useState('');
  // Collapsed until asked for: the bio is optional, and an open textarea under
  // the council record read as one more thing the doctor had to fill in.
  const [bioOpen, setBioOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The code the card on screen belongs to. Without it, editing the number
  // after a successful lookup would leave the previous doctor's name sitting
  // under a different number — and «ادامه» would still be enabled.
  const shownFor = useRef('');

  // The value of the field right now, readable from a timer callback that was
  // scheduled one or more keystrokes ago.
  const codeRef = useRef('');
  // The value the last request was sent for — set BEFORE the await, so it also
  // means "in flight" and one number is never asked twice.
  const askedRef = useRef('');
  // …except when the answer was UNAVAILABLE, which is ours and transient: a
  // blur must be able to try that same number again.
  const retryableRef = useRef(false);
  // Latest-wins. A doctor who types «12500», pauses long enough for it to be
  // sent, then adds a «0» must never be shown the record for «12500» because
  // it came back second.
  const seqRef = useRef(0);
  const pauseRef = useRef(null);

  useEffect(() => () => clearTimeout(pauseRef.current), []);

  const runLookup = async (raw) => {
    const asked = String(raw ?? '').trim();
    if (!asked || !councilDigits(asked)) return;
    if (asked === askedRef.current && !retryableRef.current) return;

    askedRef.current = asked;
    retryableRef.current = false;
    const seq = ++seqRef.current;
    setLooking(true);
    setError(null);
    setMember(null);

    // True while this answer is still the one being waited for. A superseded
    // request returns without touching the screen at all — including `looking`,
    // which the newer request owns.
    const current = () => seq === seqRef.current && codeRef.current.trim() === asked;

    try {
      const res = await bffQuery(LOOKUP_COUNCIL_MEMBER, { code: asked });
      if (!current()) return;
      const result = res?.lookupCouncilMember;
      shownFor.current = asked;
      if (result?.status === 'FOUND' && result.member) {
        setMember(result.member);
      } else if (result?.status === 'AMBIGUOUS') {
        setError(t('auth.onboarding.codeAmbiguous'));
      } else if (result?.status === 'NOT_FOUND') {
        setError(t('auth.onboarding.codeNotFound'));
      } else {
        // UNAVAILABLE — ours to fix, not the doctor's. Never «کد اشتباه است».
        retryableRef.current = true;
        setError(t('auth.onboarding.registryUnavailable'));
      }
      setLooking(false);
    } catch (e) {
      if (!current()) return;
      shownFor.current = '';
      retryableRef.current = true;
      setError(e.message || t('auth.onboarding.registryUnavailable'));
      setLooking(false);
    }
  };

  const setCodeField = (value) => {
    setCode(value);
    codeRef.current = value;
    clearTimeout(pauseRef.current);

    if (value.trim() !== shownFor.current) {
      setMember(null);
      setError(null);
    }

    const digits = councilDigits(value);
    if (digits.length >= IMMEDIATE_DIGITS) {
      runLookup(value);
    } else if (digits.length >= AUTO_MIN_DIGITS) {
      pauseRef.current = setTimeout(() => runLookup(codeRef.current), TYPING_PAUSE_MS);
    }
  };

  // Leaving the field asks for whatever is in it, however short — the one way a
  // doctor with a two- or three-digit number gets a lookup at all.
  const handleBlur = () => {
    clearTimeout(pauseRef.current);
    runLookup(codeRef.current);
  };

  const handleNext = async () => {
    if (!member) { setError(t('validation.medicalCodeRequired')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await bffMutation(COMPLETE_STEP1, {
        input: {
          // The council's own spelling, not what was typed.
          medicalCode: member.councilNumber,
          bio: bio.trim() || undefined,
        },
      });
      const doctor = res?.completeOnboardingStep1;
      if (!doctor?.id) throw new Error(t('errors.save.doctorProfile'));
      // There is no «تایید» screen after this any more: saving IS finishing.
      // `loading` stays true through `onDone`, because the host refreshes the
      // token (and may wait for the role to propagate) before the panel opens,
      // and a button that re-enabled in that gap would invite a second submit.
      await onDone({ doctor });
      return;
    } catch (e) {
      const code = e.extensions?.code;
      if (code === 'DUPLICATE_MEDICAL_CODE')          setError(t('auth.onboarding.codeTaken'));
      else if (code === 'COUNCIL_CODE_NOT_FOUND')     setError(t('auth.onboarding.codeNotFound'));
      else if (code === 'COUNCIL_CODE_AMBIGUOUS')     setError(t('auth.onboarding.codeAmbiguous'));
      else if (code === 'COUNCIL_REGISTRY_UNAVAILABLE') setError(t('auth.onboarding.registryUnavailable'));
      else setError(e.message || t('errors.save.generic'));
    }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <WelcomeHeader />

      <div style={{ marginBottom: 4, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>{t('auth.onboarding.professional')}</div>
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{t('auth.onboarding.registryPrompt')}</div>
      </div>

      <div>
        <TextInput
          label={t('common.labels.medicalCode')}
          value={code}
          onChange={setCodeField}
          onBlur={handleBlur}
          placeholder={t('settings.profile.medicalCodeExample')}
          required
          dir="ltr"
          data-testid="onboarding-medical-code"
        />
        {/* The field stays enabled while this shows: the doctor can keep
            typing, and the newer number supersedes the one being waited for.
            The text says WHAT is happening — «در حال استعلام...» alone did not
            tell a doctor who was being asked or why they were waiting — and
            the spinner says that it is still happening. */}
        <div
          data-testid="onboarding-lookup-status"
          aria-live="polite"
          aria-busy={looking || undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: '0.75rem', color: C.navyDark, marginTop: 6, minHeight: 18, lineHeight: 1.6,
          }}
        >
          {looking && (
            <>
              <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Spinner size={14} thickness={2} color={C.navy} label="" />
              </span>
              {t('auth.onboarding.lookingUp')}
            </>
          )}
        </div>
      </div>

      {member && (
        <div
          data-testid="onboarding-council-record"
          style={{
            border: `1px solid ${C.border}`, borderRadius: 10,
            padding: '12px 14px', background: soft(C.teal, 0.06),
            display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.teal, marginBottom: 6 }}>
            {t('auth.onboarding.registryTitle')}
          </div>
          <RegistryRow label={t('common.labels.fullName')} value={member.fullName} />
          <RegistryRow label={t('common.labels.medicalCode')} value={member.councilNumber} />
          {member.degree && <RegistryRow label={t('auth.onboarding.registryDegree')} value={member.degree} />}
          {member.specialty && <RegistryRow label={t('common.labels.specialty')} value={member.specialty} />}
          {member.memberType && <RegistryRow label={t('auth.onboarding.registryMemberType')} value={member.memberType} />}
          {(member.province || member.city) && (
            <RegistryRow
              label={t('auth.onboarding.registryCity')}
              value={[member.province, member.city].filter(Boolean).join(' — ')}
            />
          )}
          <div style={{ fontSize: '0.6875rem', color: C.textSecondary, marginTop: 8, lineHeight: 1.8 }}>
            {t('auth.onboarding.registryLocked')}
          </div>
        </div>
      )}

      <BioDisclosure open={bioOpen} onToggle={() => setBioOpen(o => !o)} value={bio} onChange={setBio} />

      <ErrorBox msg={error} />

      {/* One button, and it finishes: there is no step before this one to go
          back to, and no «تایید» step after it. */}
      <Btn fullWidth onClick={handleNext} disabled={loading || !member} testId="onboarding-next">
        {loading ? t('auth.onboarding.saving') : t('auth.onboarding.enterPanel')}
      </Btn>
    </div>
  );
};

// ── Stage: Step 1 for a doctor licensed OUTSIDE Iran (PM-0061) ───────────────
//
// A doctor who signed in with a non-Iranian number has no کد نظام پزشکی, so
// there is nothing to look up and nothing to read: this is the one form on
// which a doctor TYPES their professional identity. Name, specialty, degree,
// country and city are required; the licence number their own country issued
// is optional.
//
// Nothing is verified, and the form says so before the doctor saves rather
// than after: the profile it creates is shown as «تأییدنشده».
//
// Which form an account gets is the BFF's decision, read from its record of the
// number — rendering this one for an Iranian account would only earn a refusal
// from `completeForeignOnboarding`.
const StepForeignInfo = ({ phone, onDone }) => {
  const { t, locale } = useTranslation();
  const s = useOnboardingStrings();

  const [form, setForm] = useState(() => ({
    // `specialtyCode` is a catalogue row's code, or OTHER_SPECIALTY with the
    // doctor's own words in `specialty` (PM-0074).
    fullName: '', specialtyCode: '', specialty: '', degree: '',
    // The country of the number they signed in with is the likeliest issuer of
    // their licence, so it is offered — and stays theirs to change.
    country: countryForPhone(phone)?.iso2 || '',
    city: '', licenseNumber: '',
  }));
  const [bio, setBio] = useState('');
  const [bioOpen, setBioOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The catalogue (PM-0074). `null` = still loading; `[]` = unavailable (a BFF
  // older than the query), in which case the form falls back to the free-text
  // box it had before — onboarding must never be blocked by a list.
  const [catalogue, setCatalogue] = useState(null);
  useEffect(() => {
    let alive = true;
    bffQuery(SPECIALTIES_QUERY)
      .then((data) => { if (alive) setCatalogue(data?.specialties || []); })
      .catch(() => { if (alive) setCatalogue([]); });
    return () => { alive = false; };
  }, []);
  const pickerless = Array.isArray(catalogue) && catalogue.length === 0;
  const typing = pickerless || form.specialtyCode === OTHER_SPECIALTY;

  const set = (name) => (value) => setForm(f => ({ ...f, [name]: value }));

  const specialtyGiven = typing ? form.specialty.trim() !== '' : form.specialtyCode !== '';
  const complete = specialtyGiven && ['fullName', 'degree', 'country', 'city']
    .every(name => form[name].trim() !== '');

  const handleSubmit = async () => {
    if (!complete) { setError(s('auth.onboarding.foreign.incomplete')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await bffMutation(COMPLETE_FOREIGN, {
        input: {
          fullName: form.fullName.trim(),
          // A picked row sends only its code — the BFF stores its Persian name.
          ...(pickerless
            ? { specialty: form.specialty.trim() }
            : {
              specialtyCode: form.specialtyCode,
              specialty: typing ? form.specialty.trim() : undefined,
            }),
          degree: form.degree.trim(),
          country: form.country,
          city: form.city.trim(),
          licenseNumber: form.licenseNumber.trim() || undefined,
          bio: bio.trim() || undefined,
        },
      });
      const doctor = res?.completeForeignOnboarding;
      if (!doctor?.id) throw new Error(t('errors.save.doctorProfile'));
      // As on the council form: `loading` stays true through `onDone`.
      await onDone({ doctor });
      return;
    } catch (e) {
      const code = e.extensions?.code;
      if (code === 'DUPLICATE_LICENSE_NUMBER')            setError(s('auth.onboarding.foreign.licenseTaken'));
      else if (code === 'FOREIGN_PROFILE_INCOMPLETE')     setError(s('auth.onboarding.foreign.incomplete'));
      else if (code === 'FOREIGN_ONBOARDING_NOT_ALLOWED') setError(s('auth.onboarding.foreign.notAllowed'));
      else if (code === 'SPECIALTY_UNKNOWN')              setError(s('auth.onboarding.foreign.specialtyUnknown'));
      else setError(e.message || t('errors.save.generic'));
    }
    setLoading(false);
  };

  const countries = [...COUNTRIES].sort((a, b) =>
    countryName(a, locale).localeCompare(countryName(b, locale), locale));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="onboarding-foreign-form">
      <WelcomeHeader />

      <div style={{ marginBottom: 4, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>{t('auth.onboarding.professional')}</div>
        <div style={{ fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.8 }}>{s('auth.onboarding.foreign.prompt')}</div>
      </div>

      <TextInput
        label={s('auth.onboarding.foreign.fullName')}
        value={form.fullName}
        onChange={set('fullName')}
        required
        dir="auto"
        autoComplete="name"
        maxLength={120}
        data-testid="onboarding-foreign-fullName"
      />
      {pickerless ? (
        <TextInput
          label={s('auth.onboarding.foreign.specialty')}
          value={form.specialty}
          onChange={set('specialty')}
          placeholder={s('auth.onboarding.foreign.specialtyPlaceholder')}
          required
          dir="auto"
          maxLength={120}
          data-testid="onboarding-foreign-specialty"
        />
      ) : (
        <>
          <Field label={s('auth.onboarding.foreign.specialty')} required>
            <select
              data-testid="onboarding-foreign-specialty-select"
              aria-label={s('auth.onboarding.foreign.specialty')}
              value={form.specialtyCode}
              disabled={catalogue === null}
              onChange={e => set('specialtyCode')(e.target.value)}
              style={{ ...inputBase, appearance: 'auto' }}
            >
              <option value="">{s('auth.onboarding.foreign.specialtyChoose')}</option>
              {groupSpecialties(catalogue, locale).map(g => (
                <optgroup
                  key={g.kind}
                  label={s(g.kind === SUBSPECIALTY
                    ? 'auth.onboarding.foreign.specialtyGroupSubspecialty'
                    : 'auth.onboarding.foreign.specialtyGroupSpecialty')}
                >
                  {g.rows.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </optgroup>
              ))}
              <option value={OTHER_SPECIALTY}>{s('auth.onboarding.foreign.specialtyOther')}</option>
            </select>
          </Field>
          {typing && (
            <TextInput
              label={s('auth.onboarding.foreign.specialtyOtherLabel')}
              value={form.specialty}
              onChange={set('specialty')}
              placeholder={s('auth.onboarding.foreign.specialtyPlaceholder')}
              required
              dir="auto"
              maxLength={120}
              data-testid="onboarding-foreign-specialty"
            />
          )}
        </>
      )}
      <TextInput
        label={s('auth.onboarding.foreign.degree')}
        value={form.degree}
        onChange={set('degree')}
        placeholder={s('auth.onboarding.foreign.degreePlaceholder')}
        required
        dir="auto"
        maxLength={120}
        data-testid="onboarding-foreign-degree"
      />

      <Field label={s('auth.onboarding.foreign.country')} required>
        <select
          data-testid="onboarding-foreign-country"
          aria-label={s('auth.onboarding.foreign.country')}
          value={form.country}
          onChange={e => set('country')(e.target.value)}
          style={{ ...inputBase, appearance: 'auto' }}
        >
          <option value="">{s('auth.onboarding.foreign.countryPlaceholder')}</option>
          {countries.map(c => (
            <option key={c.iso2} value={c.iso2}>{countryName(c, locale)}</option>
          ))}
        </select>
      </Field>

      <TextInput
        label={s('auth.onboarding.foreign.city')}
        value={form.city}
        onChange={set('city')}
        required
        dir="auto"
        maxLength={80}
        data-testid="onboarding-foreign-city"
      />

      <div>
        <TextInput
          label={s('auth.onboarding.foreign.license')}
          value={form.licenseNumber}
          onChange={set('licenseNumber')}
          dir="ltr"
          maxLength={40}
          data-testid="onboarding-foreign-license"
        />
        <div style={{ fontSize: '0.6875rem', color: C.textSecondary, marginTop: 4, lineHeight: 1.8 }}>
          {s('auth.onboarding.foreign.licenseHint')}
        </div>
      </div>

      <BioDisclosure open={bioOpen} onToggle={() => setBioOpen(o => !o)} value={bio} onChange={setBio} />

      {/* Said BEFORE the save: the doctor should know their profile is admitted
          on their word, not learn it from a badge afterwards. */}
      <div
        data-testid="onboarding-foreign-notice"
        style={{
          padding: '0.625rem 0.875rem', background: CT.warnSoft,
          borderRadius: '0.5rem', fontSize: '0.75rem', color: CT.warnInk, lineHeight: 1.8,
        }}
      >
        {s('auth.onboarding.foreign.notice')}
      </div>

      <ErrorBox msg={error} />

      <Btn fullWidth onClick={handleSubmit} disabled={loading || !complete} testId="onboarding-next">
        {loading ? t('auth.onboarding.saving') : t('auth.onboarding.enterPanel')}
      </Btn>
    </div>
  );
};

// ── The prize wheel, when a campaign has one waiting ──────────────────────────
//
// Asked for ONCE, right after step 1 succeeds, because that is the moment the
// BFF has written the practitioner record the offer is decided from
// (docs/ONBOARDING_PRIZE_WHEEL_PLAN.md §5.2). It returns [] for almost every
// doctor and it is allowed to fail: a campaign service that is down must cost a
// doctor nothing more than a wheel they never knew about, so any failure here
// falls through to the panel exactly as if no wheel were offered.
const MY_PRIZE_DRAWS = `
  query MyPrizeDraws {
    myPrizeDraws {
      id wheelCode title subtitle status
      segments { index label kind amount }
      segmentIndex kind amount smartVisits
      whatsappMessages baleMessages smsMessages retry
      balance { balance currency }
      endsAtMs
    }
  }
`;

// A wheel worth showing: not spun yet, and with enough slices to BE a wheel
// (the kit draws nothing under two, so a one-slice campaign would render an
// empty page with a button on it).
const offeredDraw = (draws) => (Array.isArray(draws) ? draws : []).find(
  (d) => d?.status === 'offered' && Array.isArray(d.segments) && d.segments.length >= 2,
) || null;

// ── Main Onboarding Component ─────────────────────────────────────────────────
//
// One screen, then the panel. The welcome is the top of the form, and the
// «تأیید» screen that followed a save — «اطلاعات شما ثبت شد» and one button —
// is gone: saving IS finishing. The prize wheel, when a campaign owes this
// doctor a spin, is the only thing that can come between the save and the panel.
export default function OnboardingScreen({ onComplete }) {
  const { t, setLocale } = useTranslation();
  const [stage, setStage] = useState('step1');
  const [draw, setDraw] = useState(null);

  // Which form this account gets. `undefined` while the BFF is being asked;
  // after that always an object, and the COUNCIL form is what every failure
  // falls back to — a BFF older than this screen, a network error, an account
  // whose number says nothing. The foreign form is only ever shown on a
  // positive answer.
  const [context, setContext] = useState(undefined);
  const setLocaleRef = useRef(setLocale);
  setLocaleRef.current = setLocale;

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => bffQuery(ONBOARDING_CONTEXT))
      .then(data => data?.onboardingContext || {})
      .catch(() => ({}))
      .then(ctx => {
        if (!live) return;
        // A doctor who is still onboarding has never seen the language picker,
        // so the language of their number's country is the best answer there
        // is (PM-0060): an Iraqi number is greeted in Arabic. The BFF stores it
        // as their setting when onboarding completes; `setLocale` ignores a tag
        // this build does not ship.
        if (ctx.impliedLanguage) setLocaleRef.current?.(ctx.impliedLanguage);
        setContext(ctx);
      });
    return () => { live = false; };
  }, []);

  const handleComplete = async () => {
    if (onComplete) await onComplete();
    else window.location.href = '/reception';
  };

  // Step 1 is saved. Before the panel, ask whether a campaign owes this doctor
  // a spin. Nothing here can keep them from finishing: no draw, a malformed
  // one, or a thrown request all end in the same place. Outside the Iranian
  // market (`market: 'intl'`, docs/rebranding/CONTRACT.md) there is no campaign
  // to ask: the wheel pays in the Iranian campaign's credit.
  const afterStep1 = async () => {
    if (!isIranMarket()) { await handleComplete(); return; }
    try {
      const data = await bffQuery(MY_PRIZE_DRAWS);
      const offered = offeredDraw(data?.myPrizeDraws);
      if (offered) {
        setDraw(offered);
        setStage('prize');
        return;
      }
    } catch {
      /* the wheel is a bonus; onboarding is not */
    }
    await handleComplete();
  };

  return (
    <OnboardingShell
      subtitle={stage === 'prize' ? t('auth.onboarding.prize.title') : t('auth.onboarding.setupTitle')}
    >
      {stage === 'step1' && context === undefined && (
        <SkeletonForm fields={2} actions={1} data-testid="onboarding-loading" />
      )}
      {/* The council form looks the number up in the Iranian medical council's
          register; outside the Iranian market (`market: 'intl'`) every doctor
          states their licence on the form for doctors licensed abroad. */}
      {stage === 'step1' && context !== undefined && (
        context.foreign || !isIranMarket()
          ? <StepForeignInfo phone={context.phone} onDone={afterStep1} />
          : <StepDoctorInfo onDone={afterStep1} />
      )}

      {stage === 'prize' && (
        <PrizeStage draw={draw} onDone={handleComplete} />
      )}
    </OnboardingShell>
  );
}
